import React, { useEffect, useState } from 'react';
import { 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  Card, 
  CardContent, 
  CardActions, 
  Divider, 
  Chip, 
  Box,
  styled,
  CircularProgress 
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, doc, getDoc, getDocs, updateDoc, query, where, limit, Timestamp } from 'firebase/firestore';
import SubscriptionCard from '../components/subscriptions/SubscriptionCard';
import { Alert } from '@mui/lab';
import { Link, useNavigate } from 'react-router-dom';

// Define styled components for MUI v5
const StyledPaper = styled(Paper)(({ theme }) => ({
   padding: theme.spacing(3),
   marginBottom: theme.spacing(2),
}));

const OrgName = styled(Typography)(({ theme }) => ({
   fontWeight: 'bold',
   marginBottom: theme.spacing(1),
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
   margin: theme.spacing(2, 0),
}));

const StyledChip = styled(Chip)(({ theme }) => ({
   margin: theme.spacing(0.5),
   fontWeight: 500,
}));

const EventCard = styled(Card)(({ theme }) => ({
   marginTop: theme.spacing(1),
   marginBottom: theme.spacing(1),
}));

const CardHeader = styled(Box)(({ theme }) => ({
   display: 'flex',
   justifyContent: 'space-between',
   alignItems: 'center',
   padding: theme.spacing(1, 2),
}));

const RootContainer = styled(Box)(({ theme }) => ({
   padding: theme.spacing(3),
}));

const PageTitle = styled(Typography)(({ theme }) => ({
   marginBottom: theme.spacing(3),
}));

const SubTitle = styled(Typography)(({ theme }) => ({
   marginBottom: theme.spacing(2),
   marginTop: theme.spacing(4),
}));

interface SubscriptionPlan {
   id: string;
   name: string;
   price: number;
   billingCycle: string;
   features?: string[];
   description?: string;
   benefits?: string[];
}

interface Organization {
   id: string;
   name: string;
   [key: string]: any; // For other org properties
}

interface Event {
   id: string;
   title: string;
   startDate: any; // You can replace 'any' with a more specific type like Date or Timestamp
   [key: string]: any; // For other event properties
}

interface Subscription {
   id: string;
   organizationId: string;
   planId: string;
   status: string;
   startDate: any; // You can replace 'any' with a more specific type
   endDate?: any;
   organization: Organization | null;
   plan: SubscriptionPlan | null;
   events: Event[];
   [key: string]: any; // For any other properties in the subscription
}

const SubscriberPage = () => {
   const { currentUser } = useAuth();
   const navigate = useNavigate();
   const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState('');

   useEffect(() => {
      const fetchSubscriptions = async () => {
         try {
            setLoading(true);

            if (!currentUser) {
               setError('User not authenticated');
               setLoading(false);
               return;
            }

            // Get user's subscriptions
            const userSubscriptionsRef = collection(db, 'users', currentUser.uid, 'subscriptions');
            const snapshot = await getDocs(userSubscriptionsRef);

            if (snapshot.empty) {
               setSubscriptions([]);
               setLoading(false);
               return;
            }

            // Fetch full subscription details and organization info
            const subscriptionsWithDetails = await Promise.all(
               snapshot.docs.map(async (dsubDoc) => {
                  const subscriptionData = dsubDoc.data();
                  const subscription: Partial<Subscription> = {
                     id: dsubDoc.id,
                     organizationId: subscriptionData.organizationId,
                     planId: subscriptionData.planId,
                     status: subscriptionData.status,
                     startDate: subscriptionData.startDate,
                     endDate: subscriptionData.endDate,
                     ...subscriptionData,
                  };

                  // Get organization details
                  const orgDocRef = doc(db, 'organizations', subscription.organizationId as string);
                  const orgDoc = await getDoc(orgDocRef);

                  // Get subscription plan details
                  const planDocRef = doc(
                     db,
                     'organizations',
                     subscription.organizationId as string,
                     'subscriptionPlans',
                     subscription.planId as string
                  );
                  const planDoc = await getDoc(planDocRef);

                  // Get upcoming organization events for this subscription
                  const eventsRef = collection(
                     db,
                     'organizations',
                     subscription.organizationId as string,
                     'events'
                  );
                  const eventsQuery = query(
                     eventsRef,
                     where('isForMembers', '==', true),
                     where('startDate', '>=', new Date()),
                     limit(5)
                  );
                  const eventsSnapshot = await getDocs(eventsQuery);

                  const events = eventsSnapshot.docs.map((doc) => ({
                     id: doc.id,
                     ...doc.data(),
                  })) as Event[];

                  return {
                     ...subscription,
                     organization: orgDoc.exists()
                        ? ({ id: orgDoc.id, ...orgDoc.data() } as Organization)
                        : null,
                     plan: planDoc.exists()
                        ? ({ id: planDoc.id, ...planDoc.data() } as SubscriptionPlan)
                        : null,
                     events,
                  } as Subscription;
               })
            );

            setSubscriptions(subscriptionsWithDetails as Subscription[]);
         } catch (err) {
            console.error('Error fetching subscriptions:', err);
            setError('Failed to load your subscriptions. Please try again later.');
         } finally {
            setLoading(false);
         }
      };

      if (currentUser) {
         fetchSubscriptions();
      }
   }, [currentUser]);

   const handleManageSubscription = async (subscriptionId: string) => {
      try {
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/manage-subscription`, {
            method: 'POST',
            body: JSON.stringify({
               subscriptionId,
               userId: currentUser?.uid,
            }),
            headers: {
               'Content-Type': 'application/json',
            },
         });

         const data = await response.json();

         if (data.success) {
            // Redirect to Stripe Customer Portal
            window.location.href = data.url;
         } else {
            setError(data.message || 'Failed to open subscription management');
         }
      } catch (error) {
         console.error('Error opening subscription management:', error);
         setError('Failed to open subscription management. Please try again later.');
      }
   };

   const handleResumeSubscription = (subscriptionPlan: string) => {
      // Redirect to subscription checkout to resume canceled subscription
      navigate(
         `/public-subscription?userId=${currentUser?.uid}&email=${currentUser?.email}&plan=${subscriptionPlan}&resume=true`
      );
   };

   const formatDate = (timestamp: Timestamp) => {
      if (!timestamp) return 'N/A';
      const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
         year: 'numeric',
         month: 'long',
         day: 'numeric',
      });
   };

   return (
      <RootContainer>
         <PageTitle variant='h4'>My Subscriptions</PageTitle>

         {error && (
            <Alert severity='error' style={{ marginBottom: '16px' }}>
               {error}
            </Alert>
         )}

         {loading ? (
            <StyledPaper>
               <Box
                  sx={{
                     display: 'flex',
                     flexDirection: 'column',
                     alignItems: 'center',
                     justifyContent: 'center',
                     minHeight: 200,
                     gap: 2,
                  }}
               >
                  <CircularProgress size={40} />
                  <Typography variant="h6" color="text.secondary">
                     Loading your subscriptions...
                  </Typography>
               </Box>
            </StyledPaper>
         ) : subscriptions.length === 0 ? (
            <StyledPaper>
               <Typography variant='h6'>You don't have any active subscriptions</Typography>
               <Typography variant='body1' style={{ marginTop: '8px', marginBottom: '16px' }}>
                  Subscribe to organizations to access their premium content and events.
               </Typography>
               <Button
                  component={Link}
                  to='/explore-subscriptions'
                  variant='contained'
                  color='primary'
               >
                  Explore Subscriptions
               </Button>
            </StyledPaper>
         ) : (
            <Grid container spacing={3}>
               {subscriptions.map((subscription) => (
                  <Grid item xs={12} key={subscription.id}>
                     <StyledPaper>
                        <OrgName variant='h5'>
                           {subscription.organization?.name || 'Organization'}
                        </OrgName>

                        <Typography variant='subtitle1' color='textSecondary'>
                           {subscription.plan?.name || 'Subscription Plan'} - {subscription.status}
                        </Typography>

                        <StyledDivider />

                        <Grid container spacing={2}>
                           <Grid item xs={12} md={6}>
                              <Typography variant='body1'>
                                 <strong>Subscription started:</strong>{' '}
                                 {formatDate(subscription.startDate)}
                              </Typography>
                              {subscription.endDate && (
                                 <Typography variant='body1'>
                                    <strong>Renews on:</strong> {formatDate(subscription.endDate)}
                                 </Typography>
                              )}
                              <Typography variant='body1'>
                                 <strong>Price:</strong> ${subscription.plan?.price || 0} /{' '}
                                 {subscription.plan?.billingCycle || 'month'}
                              </Typography>

                              <Box mt={2}>
                                 <Typography variant='body2'>
                                    <strong>Benefits:</strong>
                                 </Typography>
                                 <Box mt={1}>
                                    {subscription.plan?.benefits?.map((benefit, index) => (
                                       <StyledChip
                                          key={index}
                                          label={benefit}
                                          size='small'
                                          color='primary'
                                          variant='outlined'
                                       />
                                    ))}
                                 </Box>
                              </Box>
                           </Grid>

                           <Grid item xs={12} md={6}>
                              <Typography variant='body2'>
                                 <strong>Upcoming Member Events:</strong>
                              </Typography>

                              {subscription.events && subscription.events.length > 0 ? (
                                 <Box>
                                    {subscription.events.map((event) => (
                                       <EventCard key={event.id}>
                                          <CardHeader>
                                             <Typography variant='subtitle2'>
                                                {event.title}
                                             </Typography>
                                             <Typography variant='caption' color='textSecondary'>
                                                {formatDate(event.startDate)}
                                             </Typography>
                                          </CardHeader>
                                          <CardContent>
                                             {event.description && (
                                                <Typography variant='body2' color='textSecondary'>
                                                   {event.description.length > 100
                                                      ? `${event.description.slice(0, 100)}...`
                                                      : event.description}
                                                </Typography>
                                             )}
                                          </CardContent>
                                       </EventCard>
                                    ))}
                                    <Button
                                       component={Link}
                                       to={`/calendar?org=${subscription.organizationId}`}
                                       color='primary'
                                       size='small'
                                    >
                                       View All Events
                                    </Button>
                                 </Box>
                              ) : (
                                 <Typography variant='body2' color='textSecondary'>
                                    No upcoming events for this subscription.
                                 </Typography>
                              )}
                           </Grid>
                        </Grid>

                        <StyledDivider />

                        <CardActions>
                           <Button
                              variant='outlined'
                              color='primary'
                              component={Link}
                              to={`/organization/${subscription.organizationId}`}
                           >
                              View Organization
                           </Button>

                           {(subscription.status === 'active' ||
                              subscription.status === 'canceled') && (
                              <Button
                                 variant='outlined'
                                 color={subscription.status === 'active' ? 'secondary' : 'primary'}
                                 onClick={() => {
                                    if (subscription.status === 'canceled') {
                                       handleResumeSubscription(
                                          subscription.plan?.name ||
                                             subscription.planId ||
                                             'standard_personal_yearly_membership'
                                       );
                                    } else {
                                       handleManageSubscription(subscription.id);
                                    }
                                 }}
                              >
                                 {subscription.status === 'active'
                                    ? 'Manage Subscription'
                                    : 'Resume Subscription'}
                              </Button>
                           )}
                        </CardActions>
                     </StyledPaper>
                  </Grid>
               ))}
            </Grid>
         )}
      </RootContainer>
   );
};

export default SubscriberPage;
